<script>
	// NPM Modules
	import { navigate } from 'svelte-navaid';
	// Stores
	import RuleStore from '@/Stores/RuleStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Services
	import { getUrlParameter, setUrlParameter, paginationBuilder } from '@/Services/Helpers.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import Helmet from '@/Components/Helmet.svelte';
	import debounce from 'lodash/debounce.js';
	import TextInput from '@/Components/TextInput.svelte';
	import isEmpty from 'lodash/isEmpty.js';

	$: links = $RuleStore && !$RuleStore.id && $RuleStore.id !== null && paginationBuilder(RuleStore.getPagination());

	let keyword;
	let pageNo = getUrlParameter('page') || 1;
	let loading = getRules();

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			getRules();
			setUrlParameter('page', pageNo);
		}
	}

	function getRules() {
		if (!isEmpty(keyword)) {
			return RuleStore.searchRuleByName(keyword, { size: 50, number: pageNo });
		} else {
			return RuleStore.getRules({ size: 50, number: pageNo });
		}
	}

	function click(id) {
		navigate(`/rules/${id}`);
	}

	function searchRule() {
		pageNo = 1;
		getRules();
	}

	const debounceUpdate = debounce(searchRule, 200);
</script>

<Helmet title="Rules" />

<div>
	{#await loading}
		<Loading />
	{:then}
		{#if $ACLStore && ACLStore.granted('rules.read')}
			<div>
				<!-- Page Header -->
				<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
					<div class="flex items-center w-full">
						<h1 class="flex-1 text-xl font-bold">Rules List</h1>
						<TextInput
							className="mr-2"
							name="rule_search"
							bind:value={keyword}
							placeholder="Search rule..."
							on:keyup={debounceUpdate}
						/>
						{#if $ACLStore && ACLStore.granted('rules.create')}
							<button class="btn btn-black" on:click={() => navigate('/rules/create')}> New Rule </button>
						{/if}
					</div>
				</div>
				<!-- /Page Header -->
				<div class="p-2 mx-4 bg-gray-100 rounded">
					<Pagination {links} on:click={changePage} className="mt-2" />
					<div class="overflow-x-auto rounded">
						<table class="w-full whitespace-nowrap">
							<thead>
								<tr class="text-xs text-left text-gray-500 uppercase">
									<th class="px-6 pt-5 pb-4 font-medium">Rule</th>
									<th class="w-16 px-6 pt-5 pb-4 font-medium">Order</th>
									<th class="w-16 px-6 pt-5 pb-4 font-medium">Active</th>
									<th class="w-px px-6 pt-5 pb-4 font-medium" />
								</tr>
							</thead>
							<tbody class="bg-white">
								{#each $RuleStore.sortBy('order').all() as rule (rule.id)}
									<tr on:click={() => click(rule.id)} class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
										<td class="p-4 px-6 border-t">
											<div class="flex items-center">
												<div class="mr-2 font-semibold">{rule.name}</div>
												{#if rule.tag}
													<span class="px-4 py-2 text-sm font-semibold text-white bg-indigo-500 rounded-full">
														{rule.tag}
													</span>
												{/if}
											</div>
										</td>
										<td class="p-4 px-6 border-t">{rule.order || '-'}</td>
										<td class="p-4 px-6 border-t">
											{#if rule.active}
												<div class="px-2 py-1 bg-green-500 rounded-md text-light">Enabled</div>
											{:else}
												<div class="px-2 py-1 bg-red-500 rounded-md text-light">Disabled</div>
											{/if}
										</td>
										<td class="w-px px-6 border-t">
											<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
										</td>
									</tr>
								{:else}
									<tr>
										<td class="px-6 py-4 text-center border-t" colspan="4"> No rules found. </td>
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
					<Pagination {links} on:click={changePage} className="my-3" />
				</div>
			</div>
		{/if}
	{/await}
</div>
